/** `flow.director` — phases/waves/rounds as DATA: each phase schedules spawn
 * groups (via a point or the pipes), ends when its required entities are all
 * defeated (`clear`), its collectibles are gone / its clock runs out
 * (`bonus`), or a rule reports the phase's goal met with the `goalSignal`
 * signal (`goal` — a landing banked on a generated field), then an
 * intermission precedes the next. After the authored list
 * it loops from `loopFrom` with `phase.loop` incremented (escalation reads
 * it), or holds when `loopFrom` is -1. A phase may also carry its own
 * LAYOUT (a level/bay/stage): placed when the phase starts, replaced when the
 * next one starts. Publishes scalars `phase.number`, `phase.loop`,
 * `phase.tick`, `phase.cleared`, `phase.bonus`; signals `phase-started`,
 * `phase-cleared` and `bonus-cleared`. */
import type { JsonObject } from '../../json.js';
import { id, int, list, num, obj, oneOf, readsSignal } from '../schemaBuilders.js';
import { defineModule, num as read, type ModuleCtx } from '../module.js';
import type { SchemaIssueV1 } from '@djgg/core';
import { layoutRectSchema, layoutTintErrors, placeRect, spawnParamIssues, type LayoutRect } from './surfaces.js';

export type SpawnGroup = {
  archetype: string; count: number; via: 'pipe' | 'point';
  x: number; y: number; dir: number; delayTicks: number; everyTicks: number;
  /** Seeded extra delay in [0, jitterTicks] per spawn. */
  jitterTicks: number;
};
export type PhaseDef = {
  kind: 'clear' | 'bonus' | 'goal'; spawns: SpawnGroup[]; durationTicks: number; clearBonus: number;
  /** The phase's own placed level (empty: the persistent world.layout only). */
  layout: LayoutRect[];
};
export type DirectorConfig = {
  phases: PhaseDef[];
  loopFrom: number;
  interPhaseTicks: number;
  requiredTag: string;
  collectTag: string;
  /** Tag of leftovers swept away when a phase ends (coins, hazards). */
  sweepTag: string;
  /** The signal that completes a `goal` phase (required by one). */
  goalSignal?: string;
};

const spawnSchema = obj({ archetype: id(), count: int(1, 12), via: oneOf('pipe', 'point'), x: num(-2, 128), y: num(-2, 128),
  dir: int(-1, 1), delayTicks: int(0, 3600), everyTicks: int(0, 900), jitterTicks: int(0, 600) });

export const director = defineModule<DirectorConfig>({
  id: 'flow.director', version: 1, attach: 'global', role: 'flow',
  summary: 'Data-driven phases: per-phase layouts, scheduled spawn groups, clear/bonus completion, intermissions, looping with a loop counter.',
  config: obj({
    phases: list(obj({ kind: oneOf('clear', 'bonus', 'goal'), spawns: list(spawnSchema, 12), durationTicks: int(0, 18000), clearBonus: int(0, 100000),
      layout: list(layoutRectSchema, 96) }), 40, 1),
    loopFrom: int(-1, 39), interPhaseTicks: int(0, 600), requiredTag: id(), collectTag: id(), sweepTag: id(), goalSignal: readsSignal(),
  }, ['goalSignal']),
  requires: [], events: ['phase-start', 'phase-clear', 'bonus-start', 'bonus-perfect'],
  presentation: { 'phase-start': { cue: 'none' }, 'phase-clear': { cue: 'success', vfx: { effect: 'flash', palette: 2 } },
    'bonus-start': { cue: 'countdown' }, 'bonus-perfect': { cue: 'powerup', vfx: { effect: 'flash', palette: 2 } } },
  proofs: ['spawns-on-schedule', 'clear-when-required-defeated', 'bonus-ends-on-collect-or-clock', 'loops-with-counter', 'holds-after-last',
    'phase-layout-replaced-on-start', 'goal-phase-ends-on-signal'],
  animStates: () => [],
  mapArchetypeRefs: (c, rename) => ({ ...c, phases: c.phases.map(p => ({ ...p,
    spawns: p.spawns.map(s => ({ ...s, archetype: rename(s.archetype) })), layout: p.layout.map(r => ({ ...r, archetype: rename(r.archetype) })) })) }),
  check(c, context) {
    const errors: Array<string | SchemaIssueV1> = [];
    const tagsOf = (archetype: string) => context.archetypes[archetype]?.tags ?? [];
    if (c.loopFrom >= c.phases.length) errors.push('loopFrom must index an authored phase');
    c.phases.forEach((p, i) => {
      if (p.kind === 'bonus' && p.durationTicks === 0) errors.push(`phase ${i}: a bonus phase needs a clock`);
      if (p.kind === 'bonus' && p.spawns.length === 0) errors.push(`phase ${i}: a bonus phase must spawn its collectibles`);
      if (p.kind === 'goal' && c.goalSignal === undefined) errors.push(`phase ${i}: a goal phase needs the director's goalSignal`);
      if (p.kind === 'clear' && ![...p.spawns, ...p.layout].some(g => tagsOf(g.archetype).includes(c.requiredTag))) {
        errors.push(`phase ${i}: a clear phase must spawn or place ${c.requiredTag} entities`);
      }
      errors.push(...layoutTintErrors(p.layout, context.paletteSize, `phase ${i}: layout`));
      errors.push(...p.layout.flatMap((rect, index) => spawnParamIssues(rect.archetype,
        { w: rect.w, h: rect.h, dir: rect.dir ?? 1, ...(rect.item === undefined ? {} : { item: rect.item }) }, context,
        `config.phases[${i}].layout[${index}]`)));
      for (const [index, s] of p.spawns.entries()) {
        errors.push(...spawnParamIssues(s.archetype, { dir: s.dir }, context, `config.phases[${i}].spawns[${index}]`));
        if (s.via === 'pipe' && !context.globals.includes('world.pipes')) errors.push(`phase ${i}: pipe spawns need world.pipes`);
        const tags = tagsOf(s.archetype);
        if (p.kind === 'bonus' && !tags.includes(c.collectTag) && !tags.includes(c.requiredTag)) errors.push(`phase ${i}: bonus spawns must be ${c.collectTag}`);
      }
    });
    return errors;
  },
  hud: () => [{ id: 'phase', label: 'PHASE', kind: 'counter' }, { id: 'remaining', label: 'LEFT', kind: 'counter' },
    { id: 'clock', label: 'BONUS', kind: 'timer' }],
  hudValues(ctx) {
    const s = ctx.state, phase = ctx.config.phases[read(s, 'index')]!;
    const remaining = ctx.entities({ tag: ctx.config.requiredTag, status: 'any' }).filter(e => !e.tags.includes('defeated')).length;
    const clock = phase.kind === 'bonus' && s.status === 'running' ? Math.max(0, Math.ceil((phase.durationTicks - read(s, 'tick')) / 30)) : 0;
    return { phase: read(s, 'number'), remaining, clock };
  },
  initGlobal(ctx) {
    Object.assign(ctx.state, { index: 0, number: 1, loop: 0, tick: 0, cleared: 0, status: 'starting', timer: 0, pending: [], placed: [] });
    publish(ctx.state, ctx);
  },
  systems: {
    flow(ctx) {
      const c = ctx.config, s = ctx.state;
      if (s.status === 'held') return;
      if (s.status === 'starting' || s.status === 'intermission') {
        if (s.status === 'intermission' && read(s, 'timer') > 0) { s.timer = read(s, 'timer') - 1; return; }
        start(ctx);
      }
      const phase = c.phases[read(s, 'index')]!;
      const tick = read(s, 'tick') + 1;
      s.tick = tick;
      // Due spawns.
      const pending = s.pending as JsonObject[];
      for (let i = 0; i < pending.length;) {
        const item = pending[i]!;
        if (read(item, 'at') > tick) { i++; continue; }
        const group = phase.spawns[read(item, 'group')]!;
        const e = ctx.spawn(group.archetype, { x: group.x, y: group.y, facing: group.dir === -1 ? -1 : 1,
          status: group.via === 'pipe' ? 'hidden' : 'active', params: { dir: group.dir } });
        if (group.via === 'pipe') ctx.signal('spawn-via-pipe', e, null, {});
        pending.splice(i, 1);
      }
      // Completion.
      const required = ctx.entities({ tag: c.requiredTag, status: 'any' }).filter(e => !e.tags.includes('defeated')).length;
      const collectibles = ctx.entities({ tag: c.collectTag, status: 'any' }).length;
      let done = false;
      if (phase.kind === 'clear') done = pending.length === 0 && required === 0;
      else if (phase.kind === 'goal') done = ctx.signals(c.goalSignal!).length > 0;
      else if (pending.length === 0 && collectibles === 0) {
        done = true;
        for (let slot = 0; slot < ctx.world.playerCount; slot++) ctx.addScore(slot, phase.clearBonus);
        ctx.emit('bonus-perfect', { phase: read(s, 'number'), points: phase.clearBonus });
      } else if (tick >= phase.durationTicks) done = true;
      if (done) finish(ctx, phase.kind);
      publish(s, ctx);
    },
  },
  solverTargets(ctx) {
    return ctx.entities({ tag: ctx.config.collectTag }).map(e => ({ x: e.x, y: e.y, weight: 6, label: 'collect' }));
  },
});

function start(ctx: ModuleCtx<DirectorConfig>): void {
  const s = ctx.state, phase = ctx.config.phases[read(s, 'index')]!;
  s.status = 'running'; s.tick = 0;
  // The previous phase's layout stays through the intermission; the next
  // phase replaces it.
  for (const placedId of s.placed as string[]) {
    const old = ctx.entity(placedId);
    if (old) ctx.despawn(old, 'phase-layout');
  }
  s.placed = phase.layout.map(rect => placeRect(ctx, rect).id);
  ctx.signal('phase-started', null, null, { phase: read(s, 'number') });
  const pending: JsonObject[] = [];
  phase.spawns.forEach((g, group) => {
    for (let k = 0; k < g.count; k++) pending.push({ group, at: g.delayTicks + k * g.everyTicks + ctx.randomInt(g.jitterTicks + 1) });
  });
  s.pending = pending;
  ctx.emit(phase.kind === 'bonus' ? 'bonus-start' : 'phase-start', { phase: read(s, 'number'), loop: read(s, 'loop') });
}

function finish(ctx: ModuleCtx<DirectorConfig>, kind: PhaseDef['kind']): void {
  const c = ctx.config, s = ctx.state;
  for (const e of ctx.entities({ tag: c.sweepTag, status: 'any' })) ctx.despawn(e, 'phase-end');
  for (const e of ctx.entities({ tag: c.requiredTag, status: 'any' })) if (e.tags.includes('defeated')) ctx.despawn(e, 'phase-end');
  s.cleared = read(s, 'cleared') + 1;
  ctx.emit('phase-clear', { phase: read(s, 'number'), kind });
  ctx.signal(kind === 'bonus' ? 'bonus-cleared' : 'phase-cleared', null, null, { phase: read(s, 'number') });
  let index = read(s, 'index') + 1;
  if (index >= c.phases.length) {
    if (c.loopFrom < 0) { s.status = 'held'; return; }
    index = c.loopFrom; s.loop = read(s, 'loop') + 1;
  }
  s.index = index; s.number = read(s, 'number') + 1; s.status = 'intermission'; s.timer = c.interPhaseTicks; s.tick = 0;
}

function publish(s: JsonObject, ctx: ModuleCtx<DirectorConfig>): void {
  ctx.setScalar('phase.number', read(s, 'number'));
  ctx.setScalar('phase.loop', read(s, 'loop'));
  ctx.setScalar('phase.tick', s.status === 'running' ? read(s, 'tick') : 0);
  ctx.setScalar('phase.cleared', read(s, 'cleared'));
  ctx.setScalar('phase.bonus', ctx.config.phases[read(s, 'index')]!.kind === 'bonus' ? 1 : 0);
}
